/**
 * DE PARSER van embed-protocol v1 (structuurplan §5.2, stappen 4–9).
 *
 * `@mark2form/contracts/embed-protocol` zegt over zichzelf: *"Platte
 * constanten, geen logica — de parser en de zenders wonen in de SDK en de
 * runtime, dit bestand is uitsluitend de spelling."* Dit is die parser.
 *
 * ── WAAROM EEN PARSER EN GEEN SCHEMA ────────────────────────────────────────
 * Een `postMessage`-handler krijgt per definitie vreemde berichten binnen:
 * React DevTools, Vercel Live, Google Tag Manager, een chatwidget en de
 * browser-extensie van de bezoeker praten allemaal over hetzelfde
 * `window.addEventListener('message', …)`. GOOIEN IS HIER DUS FOUT — een
 * exception in een message-handler is een onbehandelde fout op de pagina van
 * een klant, veroorzaakt door software van een derde. Alles wat niet past
 * geeft `null`, en de aanroeper doet niets.
 *
 * Om dezelfde reden staat hier geen `zod`: deze code wordt gebundeld tot
 * `sdk/v1.js` (§8.2: IIFE, één bestand, GEEN dependencies) en draait op een
 * vreemde klantpagina. Een schemabibliotheek in die bundel is 10× de code van
 * de vijf vormen die we controleren.
 *
 * ── DE TWEE SPELLINGEN ──────────────────────────────────────────────────────
 * §5.2: *"Berichttypen heten `m2f:*`; de runtime accepteert `upgraded:*` als
 * alias gedurende de hele v1-levensduur, zodat oude loaders blijven werken."*
 * Deze parser accepteert daarom BEIDE en normaliseert naar de `m2f:`-naam, met
 * `legacy: true` als vlag voor wie wil tellen hoeveel oude loaders er nog zijn.
 *
 * De naamlijst komt uit `EMBED_BERICHTEN`; hier staat er geen tweede. Let op
 * de valkuil die het contract zelf documenteert: de nieuwe namen van stap 9
 * gebruiken een KOPPELTEKEN (`m2f:design-complete`) waar de legacy-namen een
 * UNDERSCORE hebben (`upgraded:design_complete`). Een
 * `type.replace('m2f:', 'upgraded:')` is dus fout en zou stap 9 stil kapot
 * maken — precies het soort drift waar §3 principe 3 tegen bestaat.
 */

import {
  EMBED_BERICHTEN,
  EMBED_PLAATSING_VELDEN,
  EMBED_PLAATSING_VELDEN_PER_BERICHT,
  HOOGTE_MAX_PX,
  HOOGTE_MIN_PX,
  OFFERTE_TOKEN_MAX_LENGTE,
  OFFERTE_TOKEN_MIN_LENGTE,
  PLAATSING_NIET_BESCHIKBAAR,
  WINKELWAGEN_LABEL_MAX,
  WINKELWAGEN_REGELS_MAX,
  WINKELWAGEN_TITEL_MAX,
  WINKELWAGEN_WAARDE_MAX,
  isEmbedPlaatsingVersie,
  isOfferteId,
  isWinkelwagenModus,
  isEmbedPlaatsingWeigercode,
  isPlaatsingInstanceId,
  isPlaatsingPubliekeId,
  plaatsingVeldenFout,
  type EmbedBedrag,
  type EmbedBerichtNaam,
  type EmbedDesignCompletePayload,
  type EmbedPlaatsingVeld,
  type EmbedPlaatsingWeigercode,
  type WinkelwagenModus,
} from "@mark2form/contracts/embed-protocol";
// De GESLOTEN muntenlijst — dezelfde die `EmbedBedragSchema` als `z.enum`
// gebruikt. Dit is een RUNTIME-import en dat mag: `neon-domain` heeft één
// import en die is `import type`, dus er komt geen zod mee de bundel in. Het
// schema zelf (`@mark2form/contracts/embed-design-complete`) importeren zou dat
// wél doen — zie het docblok bij `leesBedrag`.
import { SUPPORTED_CURRENCIES } from "@mark2form/contracts/neon-domain";

/* ══════════════════════════════════════════════════════════════════════════
   DE PAYLOADVORMEN PER BERICHT
   ══════════════════════════════════════════════════════════════════════════ */

/** READY en REQUEST_CLOSE dragen alleen `type` — er valt niets te lezen. */
export type LeegPayload = Readonly<Record<string, never>>;

/**
 * §5.2 stap 5: `{type, key?, mode:'key'|'cookie', parentOrigin}`.
 *
 * ── LET OP: TWEE VERSCHILLENDE `mode`s ──────────────────────────────────────
 * De `mode` HIER is het SESSIEPAD (`key` of `cookie`). Het custom element
 * heeft óók een attribuut `mode`, en dat is de PRODUCTMODUS
 * (`logo|tekst|auto`, §5.2 stap 2). Zelfde woord, andere betekenis, andere
 * laag — vandaar deze waarschuwing op beide plekken.
 *
 * ── WAAROM `parentOrigin` HIER `beweerdeParentOrigin` HEET ──────────────────
 * Op de draad heet het veld `parentOrigin` (§5.2 stap 5), en het element zet
 * het. Maar §3 principe 6 en de ontvanger in `embed-session-client.ts` zijn
 * glashelder: *"de ontvanger leest ALLEEN `event.origin`, nooit het veld"* —
 * de browser zet `event.origin`, de afzender zet dit. Een aanvallerspagina die
 * de iframe embedt kan hier `https://klant.nl` neerzetten en zo de
 * origin-whitelist van het portaal proberen te passeren.
 *
 * Een parser mag het veld niet stilletjes weglaten (dan kan niemand een
 * bericht loggen of doorgeven), maar hij mag het ook niet `parentOrigin`
 * noemen: dat leest als een feit terwijl het een bewéring is. De naam is de
 * waarschuwing.
 */
export interface InitPayload {
  /** Niet-lege api_key; ontbreekt op het cookie-pad. */
  readonly key?: string;
  /** Het sessiepad — NIET de productmodus. */
  readonly mode: "key" | "cookie";
  /** BEWERING van de afzender. Gebruik `event.origin`. Zie het docblok. */
  readonly beweerdeParentOrigin?: string;
  /**
   * Shopkoppelingen (WP K1): de pagina belooft de winkelwagen af te handelen
   * (`<mark2form-configurator cart="host">`). Een onbekende waarde telt als
   * afwezig, zoals een oudere `v1.js` hem ook niet zou sturen.
   */
  readonly winkelwagen?: WinkelwagenModus;
}

/** §5.2 stap 8: `{height}`, al geklemd op HOOGTE_MIN_PX/HOOGTE_MAX_PX. */
export interface ResizePayload {
  readonly height: number;
}

/**
 * §5.2 stap 9 — de velden van het contract, plus de restpost.
 *
 * De vorm is `EmbedDesignCompletePayload` uit
 * `@mark2form/contracts/embed-design-complete`, hier binnengekomen via de
 * type-doorgifte onderaan `./embed-protocol` (issue #56, gesloten). De SDK
 * spelt dus géén enkel veld zelf.
 *
 * ── `total_price` IS GENEST, EN DAT IS HET HELE PUNT ────────────────────────
 * `{ amount, currency }`, niet `total_price` + `currency` naast elkaar. Plat
 * laat één stand toe die niemand wil en niemand ziet: een BEDRAG ZONDER MUNT.
 * De ontvanger — een checkout, de Shopify-app, het script van een klant — moet
 * er dan iets van maken, en het enige wat hij kan doen is euro's aannemen.
 * Dat is precies de aanname die de eigenaar verwierp (de munt is een instelling
 * in het profiel van de klant, platformbreed), alleen dan stilletjes en aan de
 * andere kant van de lijn. Als object is die stand onmogelijk: `total_price` is
 * optioneel als GEHEEL, en wie hem stuurt stuurt allebei de velden.
 *
 * `currency` is daarom ook de GESLOTEN `Currency`-unie en geen vrije string:
 * "EURO", "eur" en "€" zijn geen munt, en een `Intl.NumberFormat` die daarop
 * gooit doet dat op de pagina van de klant.
 *
 * Het bedrag staat in de HOOFDEENHEID (€ 12,50 ⇒ `12.5`) en is **nooit** de
 * bron van waarheid voor een factuur: het komt via `postMessage` binnen en is
 * dus per definitie aanvallersinvoer. Afrekenen gaat over de server
 * (`VerifiedPrice`). Lees het docblok in het contract voordat je dit veld
 * gebruikt.
 *
 * ── WAAROM `overige` BLIJFT ─────────────────────────────────────────────────
 * Het contract is `z.object` en geen `z.strictObject`: §8.2 belooft dat minors
 * van de SDK additief zijn en op hetzelfde `v1.js`-pad landen, dus een nieuwere
 * iframe mag een veld sturen dat een oudere `v1.js` nog niet kent. Zod SNOEIT
 * zo'n veld weg; deze parser doet dat bewust NIET, want een gesnoeid veld is op
 * de klantpagina niet meer terug te halen. Het reist ongelezen mee in `overige`
 * en het element legt het plat in `event.detail`. `overige` is sinds #56 dus de
 * RESTPOST — alles wat het contract niet kent — in plaats van het hele lichaam.
 */
export interface DesignCompletePayload extends EmbedDesignCompletePayload {
  /**
   * Alles wat het contract (nog) niet kent, ongelezen doorgegeven: niet `type`,
   * niet `design_id`, niet `email` en niet `total_price`.
   */
  readonly overige: Readonly<Record<string, unknown>>;
}

/**
 * Shopkoppelingen (WP K1) — `m2f:add-to-cart`, gelezen ZONDER zod (dit bestand
 * gaat mee in `sdk/v1.js`). De vorm volgt `WinkelwagenToevoegen` in
 * `@mark2form/contracts/winkelwagen`, maar losser: alles behalve het token is
 * WEERGAVE. De winkel controleert het token server-side; wat hier staat is
 * aanvallersinvoer net als `total_price` hierboven.
 */
export interface WinkelwagenToevoegenPayload {
  readonly offerte_token: string;
  readonly offerte_id: string;
  readonly design_id: string;
  readonly titel: string;
  readonly regels: readonly { readonly label: string; readonly waarde: string }[];
  /** Alleen een https-adres; anders `null`. */
  readonly voorbeeld_url: string | null;
  /** Alleen voor weergave. */
  readonly prijs?: EmbedBedrag;
}

/** Wat elk berichttype oplevert. */
export interface EmbedPayloadPerBericht {
  readonly READY: LeegPayload;
  readonly INIT: InitPayload;
  /** K05a — de uitkomst staat in `correlatie`; de payload zelf is leeg. */
  readonly PLAATSING: LeegPayload;
  readonly RESIZE: ResizePayload;
  readonly DESIGN_COMPLETE: DesignCompletePayload;
  readonly REQUEST_CLOSE: LeegPayload;
  readonly ADD_TO_CART: WinkelwagenToevoegenPayload;
  /** Gaat van de pagina náár de iframe; de pagina parst hem nooit (zie `parseKern`). */
  readonly CART_RESULT: LeegPayload;
}

/**
 * De plaatsingvelden van een bericht (koppelplan K05a), gekeurd. Welke velden
 * bij welk bericht horen staat in `EMBED_PLAATSING_VELDEN_PER_BERICHT`
 * (contracts); de parser leest alleen die.
 *
 * ── DIT ZIJN VEILIGHEIDSVELDEN, GEEN PAYLOAD ────────────────────────────────
 * Een kapot `email` laat een stap-9-bericht staan (zie `leesDesignComplete`),
 * maar een kapotte `instance_id` of `plaatsing_id` maakt het HELE bericht
 * `null`. De instance zegt voor wie het bericht is; een bericht met een
 * onleesbare geadresseerde is voor niemand. Dezelfde regels als
 * `EMBED_BERICHT_SCHEMAS` in `@mark2form/contracts/plaatsing`, via de
 * predicaten en `plaatsingVeldenFout` uit het contract.
 *
 * Eén bewuste afwijking van het zod-schema, in de veilige richting: een
 * welgevormde maar ONBEKENDE weigercode (een nieuwere runtime) is nog steeds
 * een weigering en wordt `plaatsing-niet-beschikbaar`. Een weigering negeren
 * omdat we de reden niet kennen, zou betekenen dat de sleutel alsnog vertrekt.
 */
export interface EmbedCorrelatie {
  readonly instance_id?: string;
  readonly plaatsing_id?: string;
  /** Alleen op PLAATSING: de versie die het portaal voor deze sessie oploste. */
  readonly plaatsing_versie?: number;
  /** Alleen op PLAATSING. */
  readonly plaatsing_weigering?: EmbedPlaatsingWeigercode;
}

/**
 * Eén geparsed bericht. `naam` is de contractnaam (`"READY"`…), `type` is de
 * GENORMALISEERDE `m2f:`-spelling (ook als het bericht in legacy-spelling
 * binnenkwam) en `legacy` zegt welke spelling er over de draad ging.
 *
 * `correlatie` staat er alleen als het bericht plaatsingvelden droeg (K05a).
 * Een bericht zonder die velden parst dus precies zoals vóór K05a.
 *
 * De `type`-waarden komen uit `typeof EMBED_BERICHTEN` — zo staat er zelfs in
 * de TYPES geen tweede kopie van de spelling.
 */
export type EmbedBericht = {
  [N in EmbedBerichtNaam]: {
    readonly naam: N;
    readonly type: (typeof EMBED_BERICHTEN)[N]["m2f"];
    readonly legacy: boolean;
    readonly payload: EmbedPayloadPerBericht[N];
    readonly correlatie?: EmbedCorrelatie;
  };
}[EmbedBerichtNaam];

/* ══════════════════════════════════════════════════════════════════════════
   DE NAAMTABEL — één keer opgebouwd UIT het contract
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Beide spellingen → de contractnaam. Afgeleid van `EMBED_BERICHTEN`, dus een
 * zesde berichttype in contracts werkt hier automatisch mee; een tweede
 * handgeschreven lijst zou de bug van morgen zijn.
 */
const NAAM_PER_SPELLING: ReadonlyMap<string, EmbedBerichtNaam> = (() => {
  const tabel = new Map<string, EmbedBerichtNaam>();
  for (const naam of Object.keys(EMBED_BERICHTEN) as EmbedBerichtNaam[]) {
    const paar = EMBED_BERICHTEN[naam];
    tabel.set(paar.m2f, naam);
    tabel.set(paar.legacy, naam);
  }
  return tabel;
})();

/** Gedeeld, bevroren leeg payload-object — scheelt een allocatie per bericht. */
const LEEG: LeegPayload = Object.freeze({}) as LeegPayload;

/**
 * De sleutels die het contract van stap 9 zélf kent, plus `type` (dat hoort bij
 * het BERICHT, niet bij de payload). Alles daarbuiten is restpost en gaat
 * ongelezen naar `overige`.
 *
 * ── WAAROM DIT EEN OBJECT IS EN GEEN ARRAY ──────────────────────────────────
 * Een runtime-lijst is hier onvermijdelijk: `keyof` is typewerk en levert geen
 * waarde op, en het schema zelf (dat zijn sleutels wél kent) mag deze bundel
 * niet in — het draagt zod. Een LOSSE lijst zou dus stil achterlopen zodra het
 * contract een veld krijgt, en dat veld zou daarna twee keer in het resultaat
 * staan: één keer getypeerd en één keer als restpost.
 *
 * `satisfies Record<Stap9Sleutel, true>` sluit dat aan BEIDE kanten af, en dat
 * is precies waarom het een objectliteral is en geen array:
 *
 *   - een veld erbij in het contract en hier vergeten  →  ontbrekende sleutel
 *   - een sleutel hier die het contract niet kent      →  onbekende sleutel
 *
 * Bij een array zou `satisfies readonly Stap9Sleutel[]` alleen die tweede
 * vangen — een te korte lijst is nog steeds een geldige lijst.
 */
type Stap9Sleutel = "type" | keyof EmbedDesignCompletePayload;
const CONTRACT_SLEUTELS: ReadonlySet<string> = new Set(
  Object.keys({
    type: true,
    design_id: true,
    email: true,
    total_price: true,
  } satisfies Record<Stap9Sleutel, true>),
);

/** De gesloten muntenlijst als opzoeking. Eén keer gebouwd. */
const MUNTEN: ReadonlySet<string> = new Set<string>(SUPPORTED_CURRENCIES);

/* ══════════════════════════════════════════════════════════════════════════
   DE KLEM
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * De hoogteklem van §5.2 stap 8, met de grenzen uit contracts. De loader
 * vertrouwt de iframe-inhoud niet blind: een kapotte meting mag de embed niet
 * onzichtbaar maken (ondergrens) en een gek gemeten of vijandig bericht mag de
 * klantpagina niet kilometers lang maken (bovengrens).
 *
 * Los geëxporteerd omdat het element hem ook nodig heeft voor de hoogte die
 * hij zélf zet, en omdat een klem die je niet kunt testen geen klem is.
 */
export function klemEmbedHoogte(hoogte: number): number {
  return Math.max(HOOGTE_MIN_PX, Math.min(HOOGTE_MAX_PX, Math.round(hoogte)));
}

/* ══════════════════════════════════════════════════════════════════════════
   DE PARSER
   ══════════════════════════════════════════════════════════════════════════ */

/**
 * Leest `MessageEvent.data` en geeft het genormaliseerde bericht terug, of
 * `null` als het er geen is.
 *
 * GOOIT NOOIT. Elke afwijking — geen object, een array, `type` dat geen string
 * is, een onbekende naam, een ontbrekend verplicht veld — is `null`.
 *
 * Deze functie doet GEEN herkomstcontrole: `event.origin` en `event.source`
 * staan niet in `data` en zijn de verantwoordelijkheid van de aanroeper. Dat
 * is bewust: vorm en herkomst zijn twee controles, en wie ze in één functie
 * stopt vergeet er ooit één.
 */
export function parseEmbedBericht(data: unknown): EmbedBericht | null {
  if (typeof data !== "object" || data === null || Array.isArray(data)) return null;

  const record = data as Record<string, unknown>;
  const ruweType = record["type"];
  if (typeof ruweType !== "string") return null;

  const naam = NAAM_PER_SPELLING.get(ruweType);
  if (naam === undefined) return null;

  // Welke van de twee spellingen het was. Vergelijken met `paar.legacy` en niet
  // met `paar.m2f`: bij een (theoretisch) contract waarin beide gelijk zijn
  // telt "nieuw" dan als de normale stand.
  const legacy = ruweType === EMBED_BERICHTEN[naam].legacy;

  // K05a — eerst de plaatsingvelden: een kapotte geadresseerde maakt het hele
  // bericht ongeldig, wat de rest ook zegt.
  const correlatie = leesCorrelatie(naam, record);
  if (correlatie === null) return null;

  const kern = parseKern(naam, legacy, record);
  if (kern === null || correlatie === undefined) return kern;
  return { ...kern, correlatie } as EmbedBericht;
}

/** De sleutels van alle plaatsingvelden; nooit restpost in `overige`. */
const ENVELOP_SLEUTELS: ReadonlySet<string> = new Set<string>(EMBED_PLAATSING_VELDEN);

/**
 * De plaatsingvelden die bij dit bericht horen: `undefined` als er geen enkel
 * staat, `null` als er één kapot is of de samenhang niet klopt.
 * `undefined` als waarde telt als afwezig (zoals `.optional()` in zod). Een
 * PLAATSING-bericht zonder velden bestaat niet: dat is `null`.
 */
function leesCorrelatie(
  naam: EmbedBerichtNaam,
  record: Record<string, unknown>,
): EmbedCorrelatie | null | undefined {
  const velden: readonly EmbedPlaatsingVeld[] = EMBED_PLAATSING_VELDEN_PER_BERICHT[naam];
  const uit: {
    instance_id?: string;
    plaatsing_id?: string;
    plaatsing_versie?: number;
    plaatsing_weigering?: EmbedPlaatsingWeigercode;
  } = {};
  let gevonden = false;

  for (const veld of velden) {
    const waarde = record[veld];
    if (waarde === undefined) continue;
    gevonden = true;
    switch (veld) {
      case "instance_id":
        if (!isPlaatsingInstanceId(waarde)) return null;
        uit.instance_id = waarde;
        break;
      case "plaatsing_id":
        if (!isPlaatsingPubliekeId(waarde)) return null;
        uit.plaatsing_id = waarde;
        break;
      case "plaatsing_versie":
        if (!isEmbedPlaatsingVersie(waarde)) return null;
        uit.plaatsing_versie = waarde;
        break;
      case "plaatsing_weigering":
        if (typeof waarde !== "string" || waarde === "") return null;
        uit.plaatsing_weigering = isEmbedPlaatsingWeigercode(waarde) ? waarde : PLAATSING_NIET_BESCHIKBAAR;
        break;
    }
  }

  if (!gevonden) return naam === "PLAATSING" ? null : undefined;
  if (plaatsingVeldenFout(naam, uit) !== null) return null;
  return Object.freeze(uit);
}

function parseKern(
  naam: EmbedBerichtNaam,
  legacy: boolean,
  record: Record<string, unknown>,
): EmbedBericht | null {
  switch (naam) {
    case "READY":
      return { naam, type: EMBED_BERICHTEN.READY.m2f, legacy, payload: LEEG };

    case "REQUEST_CLOSE":
      return { naam, type: EMBED_BERICHTEN.REQUEST_CLOSE.m2f, legacy, payload: LEEG };

    case "PLAATSING":
      return { naam, type: EMBED_BERICHTEN.PLAATSING.m2f, legacy, payload: LEEG };

    case "INIT": {
      const payload = leesInit(record);
      return payload === null
        ? null
        : { naam, type: EMBED_BERICHTEN.INIT.m2f, legacy, payload };
    }

    case "RESIZE": {
      const hoogte = record["height"];
      // Alleen een echt getal telt. `"560"`, `NaN` en `Infinity` zijn geen
      // hoogte, en een klem op NaN levert NaN — dat zou als CSS-waarde
      // stilletjes niets doen in plaats van te falen.
      if (typeof hoogte !== "number" || !Number.isFinite(hoogte)) return null;
      return {
        naam,
        type: EMBED_BERICHTEN.RESIZE.m2f,
        legacy,
        payload: { height: klemEmbedHoogte(hoogte) },
      };
    }

    case "DESIGN_COMPLETE": {
      const payload = leesDesignComplete(record);
      return payload === null
        ? null
        : { naam, type: EMBED_BERICHTEN.DESIGN_COMPLETE.m2f, legacy, payload };
    }

    case "ADD_TO_CART": {
      const payload = leesWinkelwagenToevoegen(record);
      return payload === null
        ? null
        : { naam, type: EMBED_BERICHTEN.ADD_TO_CART.m2f, legacy, payload };
    }

    case "CART_RESULT":
      // Richting pagina → iframe. Komt er een bij de PAGINA binnen, dan stuurde
      // een iframe hem: dat is geen geldig bericht, dus negeren.
      return null;
  }
}

/** Shopkoppelingen (WP K1) — zie `WinkelwagenToevoegenPayload`. */
function leesWinkelwagenToevoegen(record: Record<string, unknown>): WinkelwagenToevoegenPayload | null {
  const token = record["offerte_token"];
  if (typeof token !== "string" || token.length < OFFERTE_TOKEN_MIN_LENGTE || token.length > OFFERTE_TOKEN_MAX_LENGTE) {
    return null;
  }
  const offerteId = record["offerte_id"];
  if (!isOfferteId(offerteId)) return null;
  const designId = record["design_id"];
  if (typeof designId !== "string" || designId.trim() === "" || designId.length > 64) return null;
  const titel = record["titel"];
  if (typeof titel !== "string" || titel === "" || titel.length > WINKELWAGEN_TITEL_MAX) return null;

  const regels: { readonly label: string; readonly waarde: string }[] = [];
  const ruweRegels = record["regels"];
  if (Array.isArray(ruweRegels)) {
    for (const ruw of ruweRegels.slice(0, WINKELWAGEN_REGELS_MAX)) {
      if (ruw === null || typeof ruw !== "object") continue;
      const label = (ruw as Record<string, unknown>)["label"];
      const waarde = (ruw as Record<string, unknown>)["waarde"];
      if (typeof label !== "string" || label === "" || label.length > WINKELWAGEN_LABEL_MAX) continue;
      if (typeof waarde !== "string" || waarde === "" || waarde.length > WINKELWAGEN_WAARDE_MAX) continue;
      regels.push(Object.freeze({ label, waarde }));
    }
  }

  const ruweUrl = record["voorbeeld_url"];
  const voorbeeldUrl = typeof ruweUrl === "string" && ruweUrl.startsWith("https://") ? ruweUrl : null;
  const prijs = leesBedrag(record["prijs"]);

  return {
    offerte_token: token,
    offerte_id: offerteId,
    design_id: designId,
    titel,
    regels: Object.freeze(regels),
    voorbeeld_url: voorbeeldUrl,
    ...(prijs === null ? {} : { prijs }),
  };
}

/**
 * §5.2 stap 5 — de twee geldige VORMEN van een init, en verder niets.
 *
 * Dit spiegelt bewust exact `waitForEmbedInit` in `embed-session-client.ts`,
 * de ontvanger die vandaag live staat:
 *
 *   - init MÉT een niet-lege `key`    → het key-pad (de v2-handshake). De
 *     `mode` wordt dan niet gelezen: de loader van vandaag stuurt hem niet, en
 *     een key ís het key-pad.
 *   - init ZONDER key, `mode:'cookie'` → het cookie-pad (C8).
 *   - al het andere                    → `null`.
 *
 * Die laatste regel is een beveiliging, geen netheid: een keyloze init zónder
 * marker blijft genegeerd, zodat een derde frame dat een leeg init-bericht
 * roept nooit de wachttijd van een echte loader kan opsnoepen.
 */
function leesInit(record: Record<string, unknown>): InitPayload | null {
  const ruweKey = record["key"];
  const heeftKey = typeof ruweKey === "string" && ruweKey !== "";
  const beweerd = record["parentOrigin"];
  const beweerdeParentOrigin = typeof beweerd === "string" && beweerd !== "" ? beweerd : undefined;
  const ruweWinkelwagen = record["winkelwagen"];
  const winkelwagen = isWinkelwagenModus(ruweWinkelwagen) ? ruweWinkelwagen : undefined;

  if (heeftKey) {
    return {
      key: ruweKey as string,
      mode: "key",
      ...(beweerdeParentOrigin === undefined ? {} : { beweerdeParentOrigin }),
      ...(winkelwagen === undefined ? {} : { winkelwagen }),
    };
  }
  if (record["mode"] === "cookie") {
    return {
      mode: "cookie",
      ...(beweerdeParentOrigin === undefined ? {} : { beweerdeParentOrigin }),
      ...(winkelwagen === undefined ? {} : { winkelwagen }),
    };
  }
  return null;
}

/**
 * Hoort deze string bij de gesloten muntenlijst? Een typepredicaat en geen
 * `as`-cast: na een `Set.has()` versmalt TypeScript niets, en een cast zou
 * betekenen dat het TYPE `Currency` zegt terwijl er van alles in kan zitten —
 * precies de leugen die `z.enum` in het contract voorkomt.
 */
function isMunt(waarde: string): waarde is EmbedBedrag["currency"] {
  return MUNTEN.has(waarde);
}

/**
 * Het GENESTE bedrag van stap 9: `{amount, currency}`, allebei of geen van
 * beide. Zie het docblok bij `DesignCompletePayload` voor waarom dat geen
 * niveau te veel is maar het hele punt.
 *
 * ── WAAROM DIT MET DE HAND GAAT EN NIET MET `EmbedBedragSchema` ─────────────
 * Het schema staat in `@mark2form/contracts/embed-design-complete` en dat
 * bestand importeert `zod`. Deze module wordt gebundeld tot `sdk/v1.js` (§8.2:
 * één bestand, geen dependencies) en `packages/embed/bundel-controle.mjs` faalt
 * op elke overgebleven import — zod erbij is ruwweg een verzesvoudiging van het
 * bestand dat op de pagina van een klant staat. Het contract documenteert die
 * scheiding zelf: `./embed-protocol` geeft de TYPES door (een `export type`
 * verdwijnt volledig uit de emit), het schema blijft voor wie niet in een
 * browserbundel zit. De enige WAARDE die hier binnenkomt is
 * `SUPPORTED_CURRENCIES`, en dat is dezelfde lijst waar het schema zijn
 * `z.enum` op bouwt — geen tweede kopie.
 *
 * `amount` moet een eindig, niet-negatief getal zijn: `z.number()` weigert in
 * zod 4 zelf al `NaN` en `Infinity`, en `.nonnegative()` staat in het schema.
 * Een korting is een lagere prijs, geen negatief totaal.
 */
function leesBedrag(ruw: unknown): EmbedBedrag | null {
  if (typeof ruw !== "object" || ruw === null || Array.isArray(ruw)) return null;

  const record = ruw as Record<string, unknown>;
  const amount = record["amount"];
  if (typeof amount !== "number" || !Number.isFinite(amount) || amount < 0) return null;

  const currency = record["currency"];
  if (typeof currency !== "string" || !isMunt(currency)) return null;

  return { amount, currency };
}

/**
 * §5.2 stap 9 — `{design_id, email?, total_price?}` (issue #56), plus de
 * restpost. Zie het docblok bij `DesignCompletePayload`.
 *
 * `design_id` is het enige HARDE veld: zonder id is het bericht waardeloos —
 * er valt niets op te halen, niets te bestellen en niets terug te vinden — dus
 * dan is het hele bericht `null`.
 *
 * `trim()` in de controle en niet in de waarde: `"  "` is geen id, maar een id
 * dat toevallig met een spatie is verstuurd hoort ongewijzigd door te reizen —
 * wij weten niet wat de andere kant ermee doet. Het contract schrijft dezelfde
 * regel als `regex(/\S/)` zonder `.trim()`. `email` volgt hem letterlijk.
 *
 * ── EEN KAPOT OPTIONEEL VELD LAAT HET BERICHT STAAN ─────────────────────────
 * `email` of `total_price` die niet aan het contract voldoet, wordt WEGGELATEN;
 * het bericht zelf komt gewoon door. Zod zou hier het hele object afwijzen, en
 * dat is precies wat je hier NIET wilt: een bericht weggooien kost de bezoeker
 * zijn afgeronde ontwerp (de `design_id` is het haakje voor de checkout),
 * terwijl een ontbrekende prijs een stand is die het contract zelf toestaat —
 * `total_price` is optioneel. Een prijs die via `postMessage` binnenkomt is
 * bovendien nooit de bron van waarheid: er valt door hem te laten vallen niets
 * te verliezen wat de server niet beter weet. Het onleesbare veld verdwijnt
 * niet: het is een sleutel die het contract kent, dus hij gaat niet naar
 * `overige`, maar hij is aan de zendkant wel een bug — en dat is waar hij
 * hoort te worden opgelost.
 */
function leesDesignComplete(record: Record<string, unknown>): DesignCompletePayload | null {
  const designId = record["design_id"];
  if (typeof designId !== "string" || designId.trim() === "") return null;

  const ruweEmail = record["email"];
  const email = typeof ruweEmail === "string" && ruweEmail.trim() !== "" ? ruweEmail : undefined;
  const bedrag = leesBedrag(record["total_price"]);

  const overige: Record<string, unknown> = {};
  for (const sleutel of Object.keys(record)) {
    if (CONTRACT_SLEUTELS.has(sleutel)) continue;
    // K05a — de plaatsingvelden zijn de ENVELOP van het bericht, geen
    // payload: ze staan in `correlatie` en gaan niet mee naar `event.detail`.
    if (ENVELOP_SLEUTELS.has(sleutel)) continue;
    overige[sleutel] = record[sleutel];
  }

  return {
    design_id: designId,
    ...(email === undefined ? {} : { email }),
    ...(bedrag === null ? {} : { total_price: bedrag }),
    overige: Object.freeze(overige),
  };
}
